import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useHistory } from 'react-router';
import { ConfirmationModal, Typography, useOverlayContext } from 'seagull';
import ConfirmExternallySignModal from '~/components/ConfirmExternallySignModal/ConfirmExternallySignModal';
import MissingSignaturesRecipients from '~/components/MissingSignaturesRecipients/MissingSignaturesRecipients';
import RestfulWrapper from '~/components/RestfulWrapper/RestfulWrapper';
import { routes } from '~/constants';
import { useEventReporter, useStoreActions } from '~/contexts';
import { useDraftProposal } from '~/hooks/reactQuery/mutations/useDraftProposal';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { ErrorResponse, ServerError } from '~/infra/ErrorResponse';
import { useDuplicateProposal } from './reactQuery/mutations/useDuplicateProposal';
import { useCopyProposalLink } from './useCopyProposalLink';
import { useFeatureFlags } from './useFeatureFlags';
import { useSendReminder } from './useSendReminder';
import { useTerminateAgreement } from './useTerminateAgreement';
import { useWithdrawToEditProposal } from './useWithdrawToEditProposal';
const MissingSignaturesReminderModal = (param)=>{
    let { relationship, dismissModal } = param;
    const { t: proposalActionsTexts } = useLocaleKeys('hooks.proposalActions');
    const { t: commonButtons } = useLocaleKeys('common.buttons');
    const { sendReminder } = useSendReminder();
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        bi: {
            primaryButton: {
                name: 'send_proposal_reminder_confirm'
            },
            secondaryButton: {
                name: 'send_proposal_reminder_cancel'
            }
        },
        textAlignCenter: false,
        title: proposalActionsTexts.sendReminder(),
        primaryButtonLabel: commonButtons.sendNow(),
        primaryButtonTestId: "send-propposal-approval-reminder-button",
        secondaryButtonLabel: commonButtons.back(),
        secondaryButtonTestId: "cancel-send-propposal-approval-reminder-button",
        onCloseButtonClick: dismissModal,
        onPrimaryButtonClick: ()=>{
            sendReminder(relationship);
            dismissModal();
        },
        onSecondaryButtonClick: dismissModal,
        content: /*#__PURE__*/ _jsxs("div", {
            "data-testid": "send-proposal-approval-reminder-modal",
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    children: proposalActionsTexts.sendReminderDescription()
                }),
                /*#__PURE__*/ _jsx(MissingSignaturesRecipients, {
                    relationship: relationship
                })
            ]
        })
    });
};
export function useProposalActions() {
    const { t: proposalActionsTexts } = useLocaleKeys('hooks.proposalActions');
    const { t: commonButtons } = useLocaleKeys('common.buttons');
    const { showModal, showErrorToast } = useOverlayContext();
    const { api } = useStoreActions();
    const { withdrawToEditProposalByIdWithConfirmation } = useWithdrawToEditProposal();
    const { EDIT_PROPOSAL_IN_PLACE } = useFeatureFlags();
    const { reportToGtm, reportToAppcues } = useEventReporter();
    const { copyAgreementLink } = useCopyProposalLink();
    const { cancelProposalById } = useTerminateAgreement();
    const history = useHistory();
    const { draftProposal } = useDraftProposal({
        onSuccess: (newDraftId)=>history.push(routes.draft(newDraftId))
    });
    const { duplicateProposal } = useDuplicateProposal({
        onSuccess: (newDraftId)=>history.push(routes.draft(newDraftId)),
        withPageLoader: true
    });
    return {
        getEditProposalAction (proposal, origin) {
            const fromProposalDashboard = origin === 'proposalsDashboard';
            return {
                text: fromProposalDashboard ? proposalActionsTexts.edit() : proposalActionsTexts.editProposal(),
                intent: Intent.EditProposal,
                disabled: proposal.isSample,
                onClick: async ()=>{
                    if (proposal.draftId == null) return;
                    if (EDIT_PROPOSAL_IN_PLACE || fromProposalDashboard) {
                        withdrawToEditProposalByIdWithConfirmation(proposal.id, proposal.draftId);
                    } else {
                        const confirmed = await showModal({
                            biName: 'proposal_actions_withdraw_to_edit_confirm',
                            closeOnClickOutside: true,
                            closeOnEscapeKey: true,
                            content: (param)=>{
                                let { dismissModal } = param;
                                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                                    bi: {
                                        primaryButton: {
                                            name: 'withdraw_to_edit_proposal_confirm'
                                        },
                                        secondaryButton: {
                                            name: 'withdraw_to_edit_proposal_cancel'
                                        }
                                    },
                                    textAlignCenter: false,
                                    skin: "danger",
                                    title: proposalActionsTexts.withdrawToEdit(),
                                    content: proposalActionsTexts.withdrawToEditContent(),
                                    primaryButtonLabel: proposalActionsTexts.withdrawAndEdit(),
                                    primaryButtonTestId: "confirm-withdraw-to-edit",
                                    secondaryButtonLabel: commonButtons.cancel(),
                                    onPrimaryButtonClick: ()=>dismissModal(true),
                                    onSecondaryButtonClick: ()=>dismissModal(false)
                                });
                            }
                        });
                        if (!confirmed) return;
                        draftProposal({
                            relationshipId: proposal.id,
                            draftId: proposal.draftId
                        });
                    }
                }
            };
        },
        getSendProposalReminderAction (proposalId, isSampleProposal) {
            return {
                text: proposalActionsTexts.sendReminder(),
                intent: Intent.SendProposalReminder,
                disabled: isSampleProposal,
                onClick: ()=>{
                    showModal({
                        biName: 'missing_signatures_reminder_modal',
                        closeOnClickOutside: true,
                        closeOnEscapeKey: true,
                        content: (param)=>{
                            let { dismissModal } = param;
                            return /*#__PURE__*/ _jsx(RestfulWrapper, {
                                fetch: ()=>api.relationship.get(proposalId),
                                renderChild: (param)=>{
                                    let { data } = param;
                                    return /*#__PURE__*/ _jsx(MissingSignaturesReminderModal, {
                                        dismissModal: dismissModal,
                                        relationship: data
                                    });
                                }
                            });
                        }
                    });
                }
            };
        },
        getDuplicateProposalAction (proposalId, param) {
            let { isSampleProposal, draftId } = param;
            return {
                text: proposalActionsTexts.duplicate(),
                intent: Intent.DuplicateProposal,
                disabled: isSampleProposal,
                onClick: ()=>{
                    duplicateProposal({
                        relationshipId: proposalId,
                        draftId
                    });
                }
            };
        },
        getCopyProposalLinkAction (proposal) {
            return {
                text: proposalActionsTexts.copyLink(),
                intent: Intent.CopyRelationshipDirectLink,
                disabled: false,
                onClick: ()=>{
                    reportToGtm('button-click', 'vendor-copy-link-proposal', {
                        progressStatus: proposal.progressStatus
                    });
                    reportToAppcues('button-click', 'vendor-copy-link-proposal');
                    copyAgreementLink({
                        agreementId: proposal.id,
                        vendorId: proposal.vendorId,
                        customerId: proposal.customerId,
                        progressStatus: proposal.progressStatus,
                        withGlobalLoader: true
                    });
                }
            };
        },
        getCancelProposalAction (proposalId, onSuccess) {
            return {
                text: proposalActionsTexts.withdraw(),
                disabled: false,
                intent: Intent.CancelProposal,
                onClick: ()=>cancelProposalById(proposalId, onSuccess)
            };
        },
        getApproveOnBehalfAction (proposal, onSuccess) {
            return {
                text: proposalActionsTexts.approveOnBehalf(),
                intent: Intent.ApproveProposalOnBehalf,
                disabled: false,
                onClick: async ()=>{
                    if (proposal.hasExplicitApprovalToS) {
                        showErrorToast(proposalActionsTexts.approveOnBehalfExplicitApprovalError(), 'approve-on-behalf-explicit-approval-error');
                        return;
                    }
                    if (proposal.hasServiceBundles) {
                        showErrorToast(proposalActionsTexts.approveOnBehalfServiceBundlesError(), 'approve-on-behalf-service-bundles-error');
                        return;
                    }
                    if (proposal.hasOptionalServices) {
                        showErrorToast(proposalActionsTexts.approveOnBehalfOptionalServicesError(), 'approve-on-behalf-optional-services-error');
                        return;
                    }
                    const confirmed = await showModal({
                        biName: 'confirm_externally_sign_modal',
                        closeOnClickOutside: true,
                        closeOnEscapeKey: true,
                        content: (param)=>{
                            let { dismissModal } = param;
                            return /*#__PURE__*/ _jsx(ConfirmExternallySignModal, {
                                dismissModal: dismissModal,
                                hasBilledImmediatelyService: proposal.hasBilledImmediatelyService
                            });
                        }
                    });
                    if (!confirmed) return;
                    try {
                        await api.relationship.approveOnBehalfOfClient(proposal.id);
                        await onSuccess();
                    } catch (e) {
                        const errorResponse = new ErrorResponse(e);
                        if (errorResponse.hasServerError(ServerError.ExplicitApprovalNotAllowedForExternalSign)) {
                            showErrorToast(proposalActionsTexts.approveOnBehalfExplicitApprovalError(), 'approve-on-behalf-explicit-approval-error');
                        } else if (errorResponse.hasServerError(ServerError.CantCreateAgreementExternallyFromDraftWithServiceBundles)) {
                            showErrorToast(proposalActionsTexts.approveOnBehalfServiceBundlesError(), 'approve-on-behalf-service-bundles-error');
                        } else {
                            showErrorToast(proposalActionsTexts.approveOnBehalfError(), 'approve-on-behalf-error');
                        }
                    }
                }
            };
        }
    };
}
